import * as React from 'react'
// libs
import { Helmet } from 'react-helmet-async'
import { useQuery } from '@apollo/client/react'
import { useRouter } from 'hooks/useRouter'
import { useDispatch, useSelector } from 'react-redux'
// components
import PageLoader from 'components/UI/Loader/PageLoader'
// context
import { RiskAPIContext } from 'containers/Risks/Risk/Edit/layers/API'
import { RiskTemplateAPIContext } from 'containers/Risks/RiskTemplate/Edit/layers/API'
// view components
import { RiskFunctions } from 'components/CTAs'
import NotFound from './components/NotFound'
import RiskLabel from 'components/PageComponents/Risks/Edit/components/RiskLabel'
import SuccessMessage from './components/SuccessMessage'
import TextFieldModal from 'components/Modals/TextFieldModal'
import RiskTemplateFields from 'components/PageComponents/RiskTemplates/Edit/components/RiskTemplateFields'
import RiskRecurrenceModal from 'components/Modals/RiskRecurrenceModal'
import FieldRecurrenceModal from 'components/Modals/ReccurenceSettingsModal'
// hooks
import { useFormInitialValues } from 'hooks'
// graphql
import { COMPANY_RISK_RESPONSE_TYPES_PUBLIC_QUERY } from 'graphql/company/queries'
// views
import { ButtonsWrapper, Container, Header, Logo, LogoContainer } from './views'
// store
import { setResponseTypes } from 'store/reducers/riskTemplates'
import { publicFormSelector } from 'store/reducers/publicRisk'
// types
import { IResponseTypesSet } from 'types/graphql'
// utils
import get from 'lodash/get'
import { omitTypenameFromObj } from 'utils/graphql'

const PublicRiskEdit: React.FC = () => {
  const router = useRouter()
  const dispatch = useDispatch()

  const { isLoading } = React.useContext(RiskTemplateAPIContext)
  const { risk, isLoading: isRiskLoading } = React.useContext(RiskAPIContext)

  const { data, loading } = useQuery(COMPANY_RISK_RESPONSE_TYPES_PUBLIC_QUERY, {
    variables: {
      domain: router.query.domain
    },
    skip: !router.query.domain
  })

  const { confirmed } = useSelector(publicFormSelector)

  const fieldValues = React.useMemo(() => risk?.fieldValueMap || [], [risk?.id])

  const { initialValues, isInitialValuesLoaded } = useFormInitialValues(isLoading || isRiskLoading, fieldValues)

  React.useEffect(() => {
    if (!data) return

    const responseTypeSets: IResponseTypesSet[] = omitTypenameFromObj(
      get(data, 'getCompanyRiskResponseTypesPublic.responseTypesSets', [])
    )

    dispatch(setResponseTypes(responseTypeSets))
  }, [data])

  if (isLoading || isRiskLoading || !isInitialValuesLoaded || loading) {
    return <PageLoader isLoading />
  }

  if (!isLoading && !risk.id) {
    return <NotFound />
  }

  return (
    <>
      <Helmet>
        <title>Skefto - Edit Form</title>
      </Helmet>
      <Header>
        <LogoContainer>
          <Logo />
        </LogoContainer>
        <RiskLabel />
        {!confirmed && (
          <ButtonsWrapper>
            <RiskFunctions />
          </ButtonsWrapper>
        )}
      </Header>
      <Container>
        {confirmed && <SuccessMessage />}
        <RiskTemplateFields initialValues={initialValues} />
      </Container>
      <TextFieldModal />

      {/* TODO: check schedule on public */}
      <RiskRecurrenceModal />
      <FieldRecurrenceModal />
    </>
  )
}

export default PublicRiskEdit
